/**
 * Copyright © 2025 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React, { useState } from 'react';
import {
  Dialog,
  CircularProgress,
  Alert,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { registerPasskey } from '../../utils/webauthn';
import mfaService from '../../api/mfaService';
import {
  StyledDialogTitle,
  StyledDialogContent,
  StyledDialogActions,
  DialogContentContainer,
  IconWrapper,
  MessageText,
  TitleText,
  AlertWrapper,
  StyledFingerprintIcon,
  SuccessIconWrapper,
  SuccessTitle,
  StyledConfirmButton,
  StyledCancelButton,
  OptionContainer,
  OptionContent,
  OptionTitle,
  OptionDescription
} from './styles';

const MfaEnrollmentModal = ({ 
  open, 
  onSuccess, 
  onSkip,
  isSkipAllowed = true,
  enforcement = 'mandatory'
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState('passkey');

  const isOptionalEnforcement = enforcement === 'optional';
  const skipButtonLabel = isOptionalEnforcement ? 'Skip for Now' : 'Cancel Login';

  const handleStartEnrollment = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await mfaService.startEnrollment();
      
      const credential = await registerPasskey(response.options);

      const deviceName = `${navigator.platform} - ${new Date().toLocaleDateString()}`;
      await mfaService.completeEnrollment(credential, deviceName);

      setActiveStep(1);
    } catch (err) {
      setError(err.message || 'Failed to set up passkey. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    onSuccess();
  };

  const handleSkip = () => {
    onSkip();
  };

  const renderMethodSelection = () => (
    <>
      <MessageText>
        Your account is protected with <strong>multi-factor authentication (MFA)</strong>.
      </MessageText>

      {isOptionalEnforcement ? (
        <MessageText>
          MFA enrollment is currently <strong>optional</strong>. You can skip
          enrollment for now, but it may be required in the future.
        </MessageText>
      ) : (
        <MessageText>
          MFA enrollment is <strong>required</strong> for your account. Clicking
          "Cancel Login" will stop the sign-in process.
        </MessageText>
      )}
      
      <FormControl component="fieldset" sx={{ width: '100%', mt: 2 }}>
        <RadioGroup
          value={selectedMethod}
          onChange={(e) => setSelectedMethod(e.target.value)}
        >
          <OptionContainer selected={selectedMethod === 'passkey'}>
            <FormControlLabel
              value="passkey"
              control={<Radio />}
              label={
                <OptionContent>
                  <OptionTitle>
                    <FingerprintIcon sx={{ fontSize: 20, mr: 1 }} />
                    Passkey or security key
                  </OptionTitle>
                  <OptionDescription>
                    Authenticate using your fingerprint, face, or PIN on your mobile device, 
                    computer or FIDO2 security key.
                  </OptionDescription>
                </OptionContent>
              }
              sx={{ width: '100%', m: 0 }}
            />
          </OptionContainer>
        </RadioGroup>
      </FormControl>
    </>
  );

  const renderEnrollmentInProgress = () => (
    <>
      <IconWrapper>
        <StyledFingerprintIcon />
      </IconWrapper>
      <MessageText>
        Setting up your passkey...
      </MessageText>
      <CircularProgress sx={{ mt: 2 }} />
    </>
  );

  const renderSuccess = () => (
    <>
      <SuccessIconWrapper>
        <CheckCircleIcon color="success" sx={{ fontSize: 48 }} />
      </SuccessIconWrapper>
      <SuccessTitle>
        Passkey Set Up Successfully!
      </SuccessTitle>
      <AlertWrapper>
        <Alert severity="success">
          Your passkey has been registered. You can now use biometric authentication to sign in.
        </Alert>
      </AlertWrapper>
      <MessageText>
        When you sign in next time, you'll be prompted to verify with your passkey.
      </MessageText>
    </>
  );

  return (
    <Dialog 
      open={open} 
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxWidth: 500
        }
      }}
    >
      <StyledDialogTitle>
        <TitleText>
          Additional verification required
        </TitleText>
      </StyledDialogTitle>

      <StyledDialogContent>
        <DialogContentContainer>
          {error && (
            <AlertWrapper>
              <Alert severity="error">
                {error}
              </Alert>
            </AlertWrapper>
          )}

          {activeStep === 0 && (
            loading ? renderEnrollmentInProgress() : renderMethodSelection()
          )}

          {activeStep === 1 && renderSuccess()}
        </DialogContentContainer>
      </StyledDialogContent>

      <StyledDialogActions>
        {activeStep === 0 && (
          <>
            {isSkipAllowed && (
              <StyledCancelButton
                onClick={handleSkip}
                disabled={loading}
              >
                {skipButtonLabel}
              </StyledCancelButton>
            )}
            <StyledConfirmButton
              variant="contained"
              onClick={handleStartEnrollment}
              disabled={loading}
              startIcon={loading ? null : <FingerprintIcon />}
            >
              {loading ? <CircularProgress size={16} color="inherit" /> : 'Continue'}
            </StyledConfirmButton>
          </>
        )}
        
        {activeStep === 1 && (
          <StyledConfirmButton
            variant="contained"
            onClick={handleComplete}
            fullWidth
          >
            Go to Dashboard
          </StyledConfirmButton>
        )}
      </StyledDialogActions>
    </Dialog>
  );
};

export default MfaEnrollmentModal;
